import React from "react";
import { Link } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";

const FOUNDER_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_verybox-staging/artifacts/bwsabbjp_1000003175.jpg";

const MILESTONES = [
  {
    y: "2025 →",
    t: "Founded Very Big Box LLC",
    d: "Business-development studio for AI-driven product work — solo founder plus an AI support system, based in Lincoln City, Oregon.",
  },
  {
    y: "2025 →",
    t: "Building allamericandog.club",
    d: "VBB's first in-house product — a community platform and service-comparison hub for dog owners. In active development on Emergent.sh.",
  },
  {
    y: "2018 – 2020",
    t: "ReSynth International · VP Business Development",
    d: "Materials & recycling technology commercialization.",
  },
  {
    y: "2014 – 2019",
    t: "Starbridge Venture Capital · Co-Founder",
    d: "Early-stage venture work in space technology, cybersecurity, and adjacent deep-tech.",
  },
  {
    y: "2014 – 2017",
    t: "Kaje / Bright Plaza · VP Strategic Business Development",
    d: "Advanced web + semantic data platform.",
  },
  {
    y: "2013 – 2016",
    t: "Integrated Space Analytics · Partner, CTO",
    d: "Space-data analytics and infrastructure.",
  },
  {
    y: "2012 – 2016",
    t: "Space Finance Group · Partner",
    d: "Financing structures for space-industry ventures.",
  },
  {
    y: "1999 – 2006",
    t: "FXT Corporation · CEO, Consulting Systems Engineer",
    d: "Software and systems consulting practice.",
  },
  {
    y: "1988 – 1990",
    t: "Carnegie Mellon Robotics Institute · Project Scientist",
    d: "Advanced robotics research work.",
  },
  {
    y: "1982 – 1986",
    t: "Audre Inc. · VP for Research & Development",
    d: "Co-founded the company; grew it through IPO. Image recognition and document conversion technology.",
  },
];

const CAPABILITIES = [
  "Software development · full stack",
  "Business development · deal structuring",
  "AI-enabled development",
  "Startup founding & operating",
  "Semantic web · linked data · RDF",
  "Systems engineering · infrastructure",
  "Robotics · image recognition · data mining",
  "Product management",
  "Space technology · cybersecurity",
];

export default function History() {
  return (
    <main data-testid="history-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="vbb-overline">
          <span className="vbb-dot" />
          History &amp; Founder
        </div>
        <h1
          className="font-display uppercase text-5xl sm:text-7xl lg:text-[8rem] leading-[0.92] tracking-tighter mt-6 pl-[0.35em]"
          data-testid="history-title"
        >
          <BoxedTitle size="lg" thick>
            Gary
          </BoxedTitle>
          <br />
          Bickford.
        </h1>
        <p className="mt-8 max-w-2xl text-[17px] leading-relaxed text-[#2a2a2a]">
          Founder of Very Big Box LLC. Four-plus decades building software,
          companies, and products — from robotics research at Carnegie Mellon
          in the late 80s, through founding and operating startups (one taken
          public), to venture work in space technology and cybersecurity, to
          the current chapter: AI-native product development from a home base
          on the Oregon coast.
        </p>
      </section>

      <section
        className="vbb-border-t vbb-border-b"
        data-testid="history-portrait"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 lg:vbb-border-r">
            <div className="aspect-[4/5] border-b border-[#0a0a0a] overflow-hidden">
              <img
                src={FOUNDER_IMG}
                alt="Gary Bickford"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8 lg:p-10 vbb-border-b lg:!border-b-0">
              <div className="font-mono-tiny text-[#6b6b6b]">
                Founder &amp; Principal · Lincoln City, Oregon
              </div>
              <div className="font-display text-2xl mt-2 uppercase">
                Gary E. Bickford
              </div>
              <a
                href="https://linkedin.com/in/garyebickford"
                target="_blank"
                rel="noopener noreferrer"
                className="vbb-link font-mono-tiny mt-4 inline-block"
                data-testid="history-linkedin-link"
              >
                LinkedIn &#8599;
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 p-10 lg:p-16 space-y-10">
            <div>
              <div className="font-mono-tiny text-[#6b6b6b]">01 — Now</div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 uppercase tracking-tight leading-tight pl-[0.35em]">
                <BoxedTitle size="md">Building</BoxedTitle> Very Big Box.
              </h2>
              <p className="mt-4 text-[16px] leading-relaxed text-[#2a2a2a]">
                Founded VBB to develop products on his own terms — starting
                with{" "}
                <a
                  href="https://allamericandog.club"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vbb-link"
                >
                  allamericandog.club
                </a>
                , a community and service-comparison platform for dog owners,
                built on Emergent.sh. Operating as a solo founder with AI as
                the second seat, and open to co-founders on UI, business
                development, and backend.
              </p>
            </div>

            <div className="vbb-border-t pt-10">
              <div className="font-mono-tiny text-[#6b6b6b]">
                02 — Career arc
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mt-3 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">Engineer.</BoxedTitle> Founder.
                Operator.
              </h3>
              <p className="mt-4 text-[16px] leading-relaxed text-[#2a2a2a]">
                Four decades and change of building useful things: robotics
                research at Carnegie Mellon and RedZone in the late 80s;
                co-founder / VP of R&amp;D at Audre Inc. (image recognition
                and document conversion — grew through IPO); consulting CEO
                at FXT Corporation through the 90s and early 2000s; partner
                or co-founder roles across Space Finance Group, Integrated
                Space Analytics, Starbridge Venture Capital, and Kaje /
                Bright Plaza; VP Business Development at ReSynth
                International. The thread that runs through it: identify the
                emerging thing, build a company or product around it, ship
                until it works.
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[#6b6b6b] italic">
                (Full chronology lives on{" "}
                <a
                  href="https://linkedin.com/in/garyebickford"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vbb-link"
                >
                  LinkedIn
                </a>
                . Selected milestones below.)
              </p>
            </div>

            <div className="vbb-border-t pt-10">
              <div className="font-mono-tiny text-[#6b6b6b]">
                03 — Capabilities
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mt-3 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">What</BoxedTitle> he brings.
              </h3>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[15px] leading-relaxed text-[#2a2a2a]">
                {CAPABILITIES.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <span className="text-[#e34234] mt-1">—</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="vbb-border-t pt-10">
              <div className="font-mono-tiny text-[#6b6b6b]">
                04 — How he thinks
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mt-3 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">Outside</BoxedTitle> the box. From
                inside one.
              </h3>
              <p className="mt-4 text-[16px] leading-relaxed text-[#2a2a2a]">
                The motto isn&apos;t a marketing line — it&apos;s a working
                theory. Every founder, engineer, and operator is shaped by
                the box of their experience. Naming it honestly is what
                makes stepping outside of it possible.
              </p>
            </div>

            <div className="vbb-border-t pt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="vbb-btn"
                data-testid="history-contact-btn"
              >
                Get in touch →
              </Link>
              <Link
                to="/services"
                className="vbb-btn vbb-btn-ghost"
                data-testid="history-services-btn"
              >
                What VBB does
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section
        className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28"
        data-testid="history-timeline"
      >
        <div className="vbb-overline">
          <span className="vbb-dot" />
          Selected milestones
        </div>
        <h2 className="font-display text-4xl sm:text-6xl mt-4 uppercase tracking-tighter leading-[0.95] pl-[0.35em]">
          <BoxedTitle size="md">Selected</BoxedTitle> markers.
        </h2>

        <div className="mt-12 vbb-border-t">
          {MILESTONES.map((m) => (
            <div
              key={m.t}
              className="grid grid-cols-12 gap-6 vbb-border-b py-8"
              data-testid={`history-milestone-${m.y}`}
            >
              <div className="col-span-12 sm:col-span-2 font-mono-tiny text-[#6b6b6b]">
                {m.y}
              </div>
              <div className="col-span-12 sm:col-span-4 font-display text-xl uppercase tracking-tight">
                {m.t}
              </div>
              <div className="col-span-12 sm:col-span-6 text-[15px] leading-relaxed text-[#2a2a2a]">
                {m.d}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-[14px] text-[#6b6b6b] italic">
          Full career and education history on{" "}
          <a
            href="https://linkedin.com/in/garyebickford"
            target="_blank"
            rel="noopener noreferrer"
            className="vbb-link"
          >
            LinkedIn
          </a>
          .
        </p>
      </section>
    </main>
  );
}
